import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Grid
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  LocalShipping as ShippingIcon,
  AccountBalance as PlatformIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as ProfitIcon,
  Calculate as CalculateIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { CalculationResult as CalculationResultType } from '../types';

// Create a styled result container
const ResultContainer = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  gap: '24px',
  margin: '16px 0',
  '& .result-paper': {
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  }
});

const Row = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  margin: '8px 0',
});

const Label = styled(Typography)({
  fontWeight: 500,
});

const Value = styled(Typography)({
  fontFamily: 'monospace',
});

interface CalculationResultProps {
  result: CalculationResultType | null;
  loading: boolean;
}

const CalculationResult: React.FC<CalculationResultProps> = ({ result, loading }) => {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" my={4}>
        <Typography>正在计算中...</Typography>
      </Box>
    );
  }


  if (!result) {
    return (
      <Paper elevation={3} sx={{ p: 3, textAlign: 'center', my: 4 }}>
        <Typography variant="h6" color="textSecondary">
          输入商品信息并点击"计算成本与售价"按钮查看结果
        </Typography>
      </Paper>
    );
  }

  const {
    purchasePrice,
    weight,
    domesticShipping,
    internationalShipping,
    internationalShippingRule,
    platformCommission,
    platformCommissionRate,
    totalCost,
    finalSellingPrice,
    profit,
    profitRate,
  } = result;

  const formatCurrency = (value: number | null | undefined) => {
    if (value === null || value === undefined) return '¥0.00';
    return `¥${value.toFixed(2)}`;
  };

  const formatPercentage = (value: number | null | undefined) => {
    if (value === null || value === undefined) return '0.00%';
    return `${value.toFixed(2)}%`;
  };

  return (
    <Paper elevation={3} sx={{ p: 3, my: 4 }}>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
        <CalculateIcon sx={{ mr: 1 }} /> 计算结果
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
        {/* 左侧：成本明细 */}
        <Box>
          <Box>
            <Typography variant="h6" gutterBottom>成本明细</Typography>
            <Divider sx={{ mb: 2 }} />

            <Row>
              <Label>采购成本：</Label>
              <Value>{formatCurrency(purchasePrice)}</Value>
            </Row>

            <Row>
              <Label>国内运费：</Label>
              <Value>{formatCurrency(domesticShipping)}</Value>
            </Row>

            <Row>
              <Label>国际运费：</Label>
              <Value>
                {internationalShippingRule ? (
                  <>
                    {internationalShippingRule.baseFee} +
                    {internationalShippingRule.ratePerGram} × {weight}g =
                    {formatCurrency(internationalShipping)}
                  </>
                ) : (
                  formatCurrency(internationalShipping)
                )}
              </Value>
            </Row>

            <Row>
              <Label>平台佣金：</Label>
              <Value>
                {platformCommissionRate ? (
                  <>
                    {formatPercentage(platformCommissionRate * 100)} × {formatCurrency(finalSellingPrice)} =
                    {formatCurrency(platformCommission)}
                  </>
                ) : (
                  formatCurrency(platformCommission)
                )}
              </Value>
            </Row>

            <Row sx={{ mt: 2, pt: 1, borderTop: '1px dashed #e0e0e0', fontWeight: 'bold' }}>
              <Label>总成本：</Label>
              <Value variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                {formatCurrency(totalCost)}
              </Value>
            </Row>

            <Row>
              <Label>最终售价：</Label>
              <Value sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {formatCurrency(finalSellingPrice)}
              </Value>
            </Row>

            <Row sx={{ mt: 1, pt: 1, borderTop: '1px dashed #e0e0e0' }}>
              <Label>利润：</Label>
              <Value sx={{
                fontWeight: 'bold',
                color: profit >= 0 ? 'success.main' : 'error.main'
              }}>
                {formatCurrency(profit)}
              </Value>
            </Row>

            <Row>
              <Label>利润率：</Label>
              <Value sx={{
                fontWeight: 'bold',
                color: profit >= 0 ? 'success.main' : 'error.main'
              }}>
                {formatPercentage(profitRate)}
              </Value>
            </Row>
          </Box>


        </Box>

        {/* 右侧：国际运费规则 */}
        {internationalShippingRule && (
          <Paper
            elevation={2}
            sx={{
              p: 2,
              bgcolor: 'rgba(25, 118, 210, 0.04)',
              borderLeft: '4px solid',
              borderColor: 'primary.main'
            }}
          >
            <Typography variant="subtitle1" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
              <InfoIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
              国际运费规则
            </Typography>
            <Box sx={{ pl: 2 }}>
              <Typography variant="body2" color="text.secondary" paragraph>
                <strong>适用规则：</strong> {internationalShippingRule.description}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>基础费用：</strong> ¥{internationalShippingRule.baseFee}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>每克费率：</strong> ¥{internationalShippingRule.ratePerGram}/g
              </Typography>
            </Box>
            <Typography variant="subtitle1" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
              <InfoIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
              当前汇率
            </Typography>
            <Box sx={{ pl: 2 }}>
              <Typography variant="body2" color="text.secondary" paragraph>
                <strong>汇率：</strong> 1 CNY ≈ 12.5 RUB
              </Typography>
            </Box>
          </Paper>
        )}
      </Box>
    </Paper>
  );
};

export default CalculationResult;
